/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { If, Then, Else, When } from "react-if";
import {
  useTable,
  useExpanded,
  useRowSelect,
  useSortBy,
  useMountedLayoutEffect,
  Cell,
} from "react-table";
import { Table, Text } from "@nordcloud/gnui";
import {
  StyledTable,
  StyledRow,
  SVGIconDark,
  SVGIconLight,
  StyledTh,
  StyledTd,
} from "./styles";
import { ITableProps, IColumn } from "./types";

// every react-table method generates 'key' prop
/* eslint-disable react/jsx-key, @typescript-eslint/no-explicit-any */
export function ReactTable({
  columns,
  data,
  onSelectedRowsChange,
  striped,
  initialState = {},
  sort = true,
  hover,
  small,
  getRowProps = () => ({}),
  mb,
  renderRowSubComponent,
  deselectRows,
  headerBackground,
}: ITableProps) {
  const expanded = React.useMemo(
    () => ({
      0: data.length === 1,
    }),
    [data]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: sort
          ? [
              {
                id: "actualCosts",
                desc: true,
              },
            ]
          : [],
        expanded,
        ...initialState,
      },
    },
    useSortBy,
    useExpanded,
    useRowSelect
  );

  useMountedLayoutEffect(() => {
    onSelectedRowsChange?.(
      selectedFlatRows.map((row) => row.original).map(({ id }: any) => id)
    );
  }, [selectedFlatRows, onSelectedRowsChange]);

  useMountedLayoutEffect(() => {
    deselectRows && toggleAllRowsSelected(false);
  }, [deselectRows]);

  const isFooter = columns?.some((item: any) => Object.hasOwn(item, "Footer"));

  if (rows?.length === 0) {
    return <Text>No data available.</Text>;
  }
  return (
    <StyledTable
      {...getTableProps()}
      striped={!!striped}
      hoverline={hover}
      small={small}
      mb={mb}
    >
      <Table.thead>
        {headerGroups.map((headerGroup) => (
          <Table.tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              return (
                <StyledTh
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{
                    width: `${column.width}`,
                    backgroundColor: headerBackground
                      ? headerBackground
                      : undefined,
                  }}
                >
                  <span>{column.render("Header")}</span>
                  <When condition={column.canSort}>
                    <If condition={column.isSorted}>
                      <Then>
                        <SVGIconDark
                          name={column.isSortedDesc ? "sortDown" : "sortUp"}
                          size="sm"
                        />
                      </Then>
                      <Else>
                        <SVGIconLight name="sort" size="sm" />
                      </Else>
                    </If>
                  </When>
                </StyledTh>
              );
            })}
          </Table.tr>
        ))}
      </Table.thead>
      <Table.tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const rowProps = row.getRowProps();

          return (
            <React.Fragment key={rowProps.key}>
              <StyledRow
                {...row.getRowProps(getRowProps(row))}
                data-testid="table-row"
              >
                {row.cells.map((cell: Cell) => {
                  const { hasRightBorder, hasLeftBorder } =
                    cell.column as unknown as IColumn;

                  return (
                    <StyledTd
                      hasLeftBorder={hasLeftBorder}
                      hasRightBorder={hasRightBorder}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </StyledTd>
                  );
                })}
              </StyledRow>
              {row.isExpanded && renderRowSubComponent?.({ row, rowProps })}
            </React.Fragment>
          );
        })}
      </Table.tbody>
      <Table.tfoot>
        {isFooter &&
          footerGroups.map((group) => (
            <Table.tr {...group.getFooterGroupProps()}>
              {group.headers.map((column) => (
                <Table.td {...column.getFooterProps()}>
                  {column.render("Footer")}
                </Table.td>
              ))}
            </Table.tr>
          ))}
      </Table.tfoot>
    </StyledTable>
  );
}
